/**
 * API Configuration
 *
 * Backend host is `api.matchpointiq.com` in production, baked in at Docker
 * build time via the `VITE_API_URL` build arg (see Dockerfile + CI workflow).
 * Local dev falls back to a `http://localhost:8000` Django runserver.
 *
 * The in-app SPA itself uses path-only routing (e.g. `/b2b/...`,
 * `/moderator/...`) — no per-section subdomains.
 */

import i18n from "../i18n";
import { ApiRequestError, extractApiErrorMessage } from "../lib/apiError";
import type { DealOutcomeView } from "../lib/dealOutcome";
import type { SupplierConsentStatus } from "../lib/supplierConsent";
import type { ConfidenceEnum, MatchExplanationLine } from "../services/match-requests/generated/model";

export const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

export const apiUrl = (path: string): string => {
  const base = API_BASE_URL.replace(/\/$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
};

/** Turn a media URL (possibly relative) into an absolute URL for display. */
export function getMediaDisplayUrl(url: string): string {
  if (!url) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = API_BASE_URL.replace(/\/$/, "");
  return url.startsWith("/") ? `${base}${url}` : `${base}/${url}`;
}

export const apiConfig = {
  baseURL: API_BASE_URL,
  timeout: 30000,
};

const getJsonHeaders = (): HeadersInit => ({
  "Content-Type": "application/json",
  Accept: "application/json",
});

const getDefaultHeaders = (): HeadersInit => ({
  Accept: "application/json",
});

/**
 * Unwrap a list-endpoint response into a plain array.
 *
 * The backend's `paginate_queryset_response` always returns
 * `{ count, next, previous, results: T[] }`, but the OpenAPI schema currently
 * declares list responses as bare `T[]`. Until the schema is corrected, this
 * helper accepts either shape so call sites stop reading `data?.[0]` and
 * silently getting `undefined` from a paginated wrapper.
 */
export function unwrapPaginated<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object" && "results" in (data as object)) {
    const results = (data as { results?: unknown }).results;
    return Array.isArray(results) ? (results as T[]) : [];
  }
  return [];
}

/** Request body for moderator login */
export interface ModeratorLoginRequest {
  email: string;
  password: string;
}

/** Moderator profile returned from login + /current */
export interface ModeratorProfile {
  id: number;
  fullName: string;
  email: string;
  role: "MODERATOR" | "SUPERMODERATOR";
  isActive: boolean;
  isSuperModerator?: boolean;
  twoFactorEnabled?: boolean;
}

/**
 * Two-step result of `moderatorLogin`. When the moderator has 2FA
 * enabled, the backend defers session creation and returns a signed
 * `challengeToken` that must be exchanged for a session via
 * `moderatorVerifyTwoFactor`. The discriminator `twoFactorRequired`
 * lets the UI branch without sniffing structural shape.
 */
export type ModeratorLoginResult =
  | { twoFactorRequired: false; profile: ModeratorProfile }
  | { twoFactorRequired: true; challengeToken: string };

/** Login as a moderator. Sets a session cookie keyed `moderator_id` when
 * 2FA is off; otherwise returns a challenge that must be completed via
 * `moderatorVerifyTwoFactor`. */
export async function moderatorLogin(body: ModeratorLoginRequest): Promise<ModeratorLoginResult> {
  const url = apiUrl("/api/v1/moderators/auth/login/");
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.invalidEmailOrPassword")),
      response.status,
      data,
    );
  }
  if ((data as { twoFactorRequired?: boolean }).twoFactorRequired) {
    return {
      twoFactorRequired: true,
      challengeToken: (data as { challengeToken: string }).challengeToken,
    };
  }
  return { twoFactorRequired: false, profile: data as ModeratorProfile };
}

/** Body of the moderator 2FA verify call. Exactly one of `code` /
 * `recoveryCode` should be present — the backend rejects the other
 * combinations with 400. */
export interface ModeratorVerifyTwoFactorRequest {
  challengeToken: string;
  code?: string;
  recoveryCode?: string;
}

/** Submit the second factor (TOTP or recovery code) and finish the
 * moderator login. Establishes the session cookie on success. */
export async function moderatorVerifyTwoFactor(body: ModeratorVerifyTwoFactorRequest): Promise<ModeratorProfile> {
  const url = apiUrl("/api/v1/moderators/auth/verifyTwoFactor/");
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.invalidCode")),
      response.status,
      data,
    );
  }
  return data as ModeratorProfile;
}

/** Log out the current moderator. */
export async function moderatorLogout(): Promise<void> {
  const url = apiUrl("/api/v1/moderators/auth/logout/");
  await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
  });
}

/** Fetch the currently logged-in moderator. Returns null on 401/403. */
export async function getCurrentModerator(): Promise<ModeratorProfile | null> {
  const url = apiUrl("/api/v1/moderators/current/");
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });
  if (response.status === 401 || response.status === 403) return null;
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadModerator")),
      response.status,
      data,
    );
  }
  return data as ModeratorProfile;
}

/** Request body for company registration */
export interface CompanyRegisterRequest {
  companyName: string;
  companyIndustry: string;
  companyEmail: string;
  companyDescription: string;
  companyAvatar: string;
}

/** Success response from company registration */
export interface CompanyRegisterResponse {
  id: number;
  companyName: string;
  companyEmail: string;
  message: string;
}

export async function registerCompany(body: CompanyRegisterRequest): Promise<CompanyRegisterResponse> {
  const url = apiUrl("/api/v1/companies/register/");
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.registrationFailed")),
      response.status,
      data,
    );
  }

  return data as CompanyRegisterResponse;
}

/** Response from file upload */
export interface FileUploadResponse {
  url: string;
  message: string;
}

/** Upload a file (e.g. company avatar). Returns the file URL to use in registration. */
export async function uploadFile(file: File): Promise<FileUploadResponse> {
  const url = apiUrl("/api/v1/common/upload/");
  const formData = new FormData();
  formData.append("file", file);

  const headers: HeadersInit = getDefaultHeaders();
  // Do not set Content-Type; browser sets multipart/form-data with boundary

  const response = await fetch(url, {
    method: "POST",
    headers,
    credentials: "include",
    body: formData,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.fileUploadFailed")),
      response.status,
      data,
    );
  }

  return data as FileUploadResponse;
}

/**
 * Upload a compliance document straight to the company documents endpoint.
 * The backend stores it in PRIVATE storage; the returned `fileUrl` is a
 * short-lived signed link (do not persist it). Never route compliance files
 * through `uploadFile()` — that endpoint stores files publicly.
 */
export async function uploadCompanyDocument(file: File): Promise<CompanyDocument> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(apiUrl("/api/v1/companies/documents/"), {
    method: "POST",
    headers: getDefaultHeaders(),
    credentials: "include",
    body: formData,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(extractApiErrorMessage(data, i18n.t("common:apiFallback.documentUploadFailed")));
  }

  return data as CompanyDocument;
}

export interface MediaPayload {
  fileUrl: string;
  mediaType: "image" | "video";
  description: string;
  order: number;
}

export interface ProductCreateRequest {
  name: string;
  description: string;
  category: string;
  quantity: number;
  price: string;
  currency: string;
  deliveryTime: string;
  region: string;
  hsCode: string;
  media: MediaPayload[];
}

export interface ProductCreateResponse {
  id: number;
  companyId: number;
  companyName: string;
  name: string;
  description: string;
  category: string;
  quantity: number;
  price: string;
  currency: string;
  deliveryTime: string;
  region: string;
  hsCode: string;
  media: Array<MediaPayload & { id: number; createdAt: string }>;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceCreateRequest {
  name: string;
  description: string;
  category: string;
  price: string;
  currency: string;
  deliveryTime: string;
  region: string;
  hsCode: string;
  media: MediaPayload[];
}

export interface ServiceCreateResponse {
  id: number;
  companyId: number;
  companyName: string;
  name: string;
  description: string;
  category: string;
  price: string;
  currency: string;
  deliveryTime: string;
  region: string;
  hsCode: string;
  media: Array<MediaPayload & { id: number; createdAt: string }>;
  createdAt: string;
  updatedAt: string;
}

export async function createProduct(body: ProductCreateRequest): Promise<ProductCreateResponse> {
  const url = apiUrl("/api/v1/products/create/");
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.productCreationFailed")),
      response.status,
      data,
    );
  }

  return data as ProductCreateResponse;
}

export async function createService(body: ServiceCreateRequest): Promise<ServiceCreateResponse> {
  const url = apiUrl("/api/v1/services/create/");
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.serviceCreationFailed")),
      response.status,
      data,
    );
  }

  return data as ServiceCreateResponse;
}

/** GET /api/v1/products/myProducts/ */
export async function getMyProducts(): Promise<ProductCreateResponse[]> {
  const url = apiUrl("/api/v1/products/myProducts/");
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadProducts")),
      response.status,
      data,
    );
  }

  return Array.isArray(data) ? data : [];
}

/** GET /api/v1/services/myServices/ */
export async function getMyServices(): Promise<ServiceCreateResponse[]> {
  const url = apiUrl("/api/v1/services/myServices/");
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadServices")),
      response.status,
      data,
    );
  }

  return Array.isArray(data) ? data : [];
}

/** DELETE /api/v1/products/{id}/delete/ */
export async function deleteProduct(id: number | string): Promise<void> {
  const url = apiUrl(`/api/v1/products/${id}/delete/`);
  const response = await fetch(url, {
    method: "DELETE",
    headers: getJsonHeaders(),
    credentials: "include",
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToDeleteProduct")),
      response.status,
      data,
    );
  }
}

/** DELETE /api/v1/services/{id}/delete/ */
export async function deleteService(id: number | string): Promise<void> {
  const url = apiUrl(`/api/v1/services/${id}/delete/`);
  const response = await fetch(url, {
    method: "DELETE",
    headers: getJsonHeaders(),
    credentials: "include",
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToDeleteService")),
      response.status,
      data,
    );
  }
}

/** PUT /api/v1/products/{id}/update/ - body same as create */
export async function updateProduct(id: number | string, body: ProductCreateRequest): Promise<ProductCreateResponse> {
  const url = apiUrl(`/api/v1/products/${id}/update/`);
  const response = await fetch(url, {
    method: "PUT",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.productUpdateFailed")),
      response.status,
      data,
    );
  }

  return data as ProductCreateResponse;
}

/** PUT /api/v1/services/{id}/update/ - body same as create */
export async function updateService(id: number | string, body: ServiceCreateRequest): Promise<ServiceCreateResponse> {
  const url = apiUrl(`/api/v1/services/${id}/update/`);
  const response = await fetch(url, {
    method: "PUT",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.serviceUpdateFailed")),
      response.status,
      data,
    );
  }

  return data as ServiceCreateResponse;
}

/** Request body for creating a match request */
export interface MatchRequestCreateRequest {
  companyId: number;
  requirementTitle: string;
  productServiceName: string;
  hsCode: string;
  quantityVolume: string;
  budgetRange: string;
  timeline: string;
  preferredRegions: string;
  additionalRequirements: string;
  industryFit: number;
  companySize: number;
  geographicProximity: number;
  complianceScore: number;
  productMatch: number;
  budgetAlignment: number;
}

/**
 * Public summary of the matched company inside a match result / conversation.
 * The backend never sends another company's email, users, compliance scores
 * or screening data (finding N10) — only these display fields.
 */
export interface MatchResultCompany {
  id: number;
  companyName: string;
  companyIndustry: string | null;
  companyDescription: string | null;
  companyAvatar: string | null;
  /** ISO 3166-1 alpha-2 code */
  country: string | null;
  region: string | null;
  /** Awarded badge key or `none` (D1-3, KYB phase 2); keys are data. Scores are not exposed. */
  trustBadgeLevel: string;
  trustBadge?: { key: string; label: string; description: string } | null;
  /** WP3.2 — disclosure sentence shown to the counterparty when this company
   * was sourced through a paid referral or revenue-share partner. Empty
   * string in every other case. Never names the partner. */
  referralDisclosure: string;
}

/** A canonical timeline milestone on a deal. */
export interface MatchResultTimelineItem {
  name: string;
  status: "pending" | "done" | "not_done";
  date: string | null;
}

/** Contract attached to a deal (1:1 with MatchResult). */
export interface MatchResultContract {
  id: number;
  status: string;
  contractType: string;
  contractValue: string | null;
  startDate: string | null;
  endDate: string | null;
  dateSigned: string | null;
  description: string;
  termsAndConditions: string;
  documents: string[];
  referenceId: string;
  companyAName: string | null;
  companyBName: string | null;
}

/**
 * The SUPPLIER's catalog product/service that produced a match (not the
 * buyer's requested name). Null on legacy deals or if the row was deleted.
 */
export interface MatchResultMatchedItem {
  type: "product" | "service";
  id: number;
  name: string;
  hsCode: string | null;
}

/** Single match result inside GET /api/v1/matchRequests/myRequests/ [].results */
export interface MatchRequestResultItem {
  id: number;
  company: MatchResultCompany;
  matchedItem: MatchResultMatchedItem | null;
  winWinMatchScore: number;
  industryMatch: number;
  geographicLocation: number;
  // Auto-filled by the backend: every milestone is always present (status
  // defaults to "pending"). Deal comments are internal moderator notes and
  // are never sent to companies (OQ-07).
  timeline?: MatchResultTimelineItem[];
  contract?: MatchResultContract | null;
  /** "Why this match" (WP1.4) — empty for older matches. */
  explanation?: MatchExplanationLine[];
  matchConfidence?: ConfidenceEnum | null;
  /** Supplier consent (WP1.6): chat opens only once "accepted". */
  consentStatus?: SupplierConsentStatus;
  consentRespondedAt?: string | null;
  /** Deal outcome (WP1.7): status + stage dates, read-only. Never the lost reason. */
  outcome?: DealOutcomeView;
  /** Deal created by a moderator between companies that never matched (OQ-12). */
  isManual?: boolean;
}

/** Single item from GET /api/v1/matchRequests/myRequests/ */
export interface MatchRequestListItem {
  id: number;
  companyId: number;
  companyName: string;
  userId: number;
  userEmail: string;
  requestType: "product" | "service";
  requirementTitle: string;
  productServiceName: string;
  hsCode: string;
  quantityVolume: string;
  budgetRange: string;
  timeline: string;
  preferredRegions: string;
  additionalRequirements: string;
  industryFit: number;
  companySize: number;
  geographicProximity: number;
  complianceScore: number;
  productMatch: number;
  budgetAlignment: number;
  status: string;
  message: string | null;
  createdAt: string;
  updatedAt: string;
  results?: MatchRequestResultItem[];
}

/** POST /api/v1/matchRequests/create/ */
export async function createMatchRequest(body: MatchRequestCreateRequest): Promise<MatchRequestListItem> {
  const url = apiUrl("/api/v1/matchRequests/create/");
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.requestCreationFailed")),
      response.status,
      data,
    );
  }

  return data as MatchRequestListItem;
}

/** GET /api/v1/matchRequests/myRequests/ */
export async function getMyMatchRequests(): Promise<MatchRequestListItem[]> {
  const url = apiUrl("/api/v1/matchRequests/myRequests/");
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadRequests")),
      response.status,
      data,
    );
  }

  return Array.isArray(data) ? data : [];
}

/** Response from GET /api/v1/matchRequests/totalMatchResults/ */
export interface TotalMatchResultsSummary {
  totalRequests: number;
  totalResults: number;
}

export interface TotalMatchResultsDataItem {
  request: MatchRequestListItem;
  results: MatchRequestResultItem[];
}

export interface TotalMatchResultsResponse {
  summary: TotalMatchResultsSummary;
  data: TotalMatchResultsDataItem[];
}

/** GET /api/v1/matchRequests/totalMatchResults/ */
export async function getTotalMatchResults(): Promise<TotalMatchResultsResponse> {
  const url = apiUrl("/api/v1/matchRequests/totalMatchResults/");
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadMatchResults")),
      response.status,
      data,
    );
  }

  return data as TotalMatchResultsResponse;
}

/** Company can be full object (from API) or string for display */
type CompanyOrName = { companyName?: string } | string;

/** Conversation (list item and detail) - API returns companyA/companyB as full company objects */
export interface Conversation {
  id: number;
  companyA: CompanyOrName;
  companyB: CompanyOrName;
  matchResult: unknown;
  lastMessageAt: string | null;
  lastMessage:
    | string
    | { id?: number; content?: string; senderCompanyName?: string; createdAt?: string; isRead?: boolean }
    | null;
  unreadCount: number;
  createdAt: string;
}

/** Extract company name from companyA/companyB (object or string) - always returns string */
export function getCompanyName(c: CompanyOrName | null | undefined): string {
  if (c == null) return i18n.t("common:states.unknown");
  if (typeof c === "string") return c;
  const name = (c as { companyName?: unknown }).companyName;
  return name != null ? String(name) : i18n.t("common:states.unknown");
}

/** Request body for POST /api/v1/conversations/create/ */
export interface ConversationCreateRequest {
  matchResultId: number;
}

/** GET /api/v1/conversations/ */
export async function getConversations(): Promise<Conversation[]> {
  const url = apiUrl("/api/v1/conversations/");
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadConversations")),
      response.status,
      data,
    );
  }
  return Array.isArray(data) ? data : [];
}

/** POST /api/v1/conversations/create/ */
export async function createConversation(body: ConversationCreateRequest): Promise<Conversation> {
  const url = apiUrl("/api/v1/conversations/create/");
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToCreateConversation")),
      response.status,
      data,
    );
  }
  return data as Conversation;
}

/** GET /api/v1/conversations/{id}/detail/ */
export async function getConversationDetail(id: number): Promise<Conversation> {
  const url = apiUrl(`/api/v1/conversations/${id}/detail/`);
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadConversation")),
      response.status,
      data,
    );
  }
  return data as Conversation;
}

/** Message in a conversation */
export interface ConversationMessage {
  id: number;
  senderId: number;
  senderName: string;
  senderCompanyId: number;
  senderCompanyName: string;
  content: string;
  readAt: string | null;
  isRead: string;
  createdAt: string;
}

/** GET /api/v1/conversations/{id}/messages/ */
export async function getConversationMessages(id: number): Promise<ConversationMessage[]> {
  const url = apiUrl(`/api/v1/conversations/${id}/messages/`);
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadMessages")),
      response.status,
      data,
    );
  }
  return Array.isArray(data) ? data : [];
}

/** Request body for POST /api/v1/conversations/{id}/send/ */
export interface SendMessageRequest {
  content: string;
}

/** POST /api/v1/conversations/{id}/send/ */
export async function sendMessage(conversationId: number, body: SendMessageRequest): Promise<ConversationMessage> {
  const url = apiUrl(`/api/v1/conversations/${conversationId}/send/`);
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToSendMessage")),
      response.status,
      data,
    );
  }
  return data as ConversationMessage;
}

/** POST /api/v1/conversations/{id}/markRead/ */
export interface MarkReadResponse {
  message: string;
  updatedCount: number;
}

export async function markConversationRead(id: number): Promise<MarkReadResponse> {
  const url = apiUrl(`/api/v1/conversations/${id}/markRead/`);
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToMarkAsRead")),
      response.status,
      data,
    );
  }
  return data as MarkReadResponse;
}

/** Single company from GET /api/v1/companies/myCompanies/ */
export interface CompanyProfile {
  id: number;
  companyName: string;
  companyIndustry: string;
  companyEmail: string;
  companyDescription: string;
  companyAvatar: string | null;
  isActive: boolean;
  isApproved: boolean;
  overallComplianceStatus?: string;
  /** null = not assessed (never shown as 0 or 50). */
  overallComplianceScore?: number | null;
  tier1BasicVerification?: number | null;
  tier2StandardCompliance?: number | null;
  tier3AdvancedDueDiligence?: number | null;
  trustBadgeLevel?: string;
  trustBadge?: { key: string; label: string; description: string } | null;
  newMatchs: boolean;
  ComplianceAlerts: boolean;
  MarketInteligenceUpdates: boolean;
  MessageNotifications: boolean;
  weeklySummaryEmail: boolean;
  primaryMarket: string;
  language: string;
  timeZone: string;
  userRole: { roleName: string; roleId: number };
  userRoles: Array<{
    id: number;
    role: { id: number; name: string; description: string };
    roleName: string;
    userId: number;
    userEmail: string;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

/** Request body for PUT /api/v1/companies/updateProfile/ */
export interface CompanyProfileUpdateRequest {
  companyName: string;
  companyIndustry: string;
  companyDescription: string;
  companyAvatar: string;
  newMatchs: boolean;
  ComplianceAlerts: boolean;
  MarketInteligenceUpdates: boolean;
  MessageNotifications: boolean;
  weeklySummaryEmail: boolean;
  primaryMarket: string;
  language: string;
  timeZone: string;
}

/** GET /api/v1/companies/myCompanies/ */
export async function getMyCompanies(): Promise<CompanyProfile[]> {
  const url = apiUrl("/api/v1/companies/myCompanies/");
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadProfile")),
      response.status,
      data,
    );
  }

  return Array.isArray(data) ? data : [];
}

/** PUT /api/v1/companies/updateProfile/ */
export async function updateCompanyProfile(body: CompanyProfileUpdateRequest): Promise<CompanyProfile> {
  const url = apiUrl("/api/v1/companies/updateProfile/");
  const response = await fetch(url, {
    method: "PUT",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.profileUpdateFailed")),
      response.status,
      data,
    );
  }

  return data as CompanyProfile;
}

/** Entity registration item from GET /api/v1/companies/entityRegistrations/ */
export interface EntityRegistration {
  id: number;
  country: string;
  registrationNumber: string;
  corporationType: string;
  /** ISO 3166-2 or UAE free zone (`CA-QC`, `AE-DMCC`); drives KYB routing. */
  subdivision?: string;
  status: string;
  registeredDate: string;
  createdAt: string;
}

/** Request body for create/update entity registration.
 * `status` is admin-managed server-side — new rows start as `pending`. */
export interface EntityRegistrationPayload {
  country: string;
  /** Optional `<country>-<code>`, e.g. `CA-QC` or `AE-DMCC` (KYB routing). */
  subdivision: string;
  registrationNumber: string;
  corporationType: string;
  registeredDate: string;
}

/** GET /api/v1/companies/entityRegistrations/ */
export async function getEntityRegistrations(): Promise<EntityRegistration[]> {
  const url = apiUrl("/api/v1/companies/entityRegistrations/");
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadEntityRegistrations")),
      response.status,
      data,
    );
  }
  return Array.isArray(data) ? data : [];
}

/** POST /api/v1/companies/entityRegistrations/ */
export async function createEntityRegistration(body: EntityRegistrationPayload): Promise<EntityRegistration> {
  const url = apiUrl("/api/v1/companies/entityRegistrations/");
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToAddEntity")),
      response.status,
      data,
    );
  }
  return data as EntityRegistration;
}

/** PUT /api/v1/companies/entityRegistrations/{id}/ */
export async function updateEntityRegistration(
  id: number,
  body: EntityRegistrationPayload,
): Promise<EntityRegistration> {
  const url = apiUrl(`/api/v1/companies/entityRegistrations/${id}/`);
  const response = await fetch(url, {
    method: "PUT",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToUpdateEntity")),
      response.status,
      data,
    );
  }
  return data as EntityRegistration;
}

/** DELETE /api/v1/companies/entityRegistrations/{id}/ */
export async function deleteEntityRegistration(id: number): Promise<void> {
  const url = apiUrl(`/api/v1/companies/entityRegistrations/${id}/`);
  const response = await fetch(url, {
    method: "DELETE",
    headers: getJsonHeaders(),
    credentials: "include",
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToDeleteEntity")),
      response.status,
      data,
    );
  }
}

/** Shareholder item from GET /api/v1/companies/shareholders/ */
export interface Shareholder {
  id: number;
  name: string;
  ownershipPercent: string;
  nationality: string;
  idVerified: string;
  uboStatus: string;
  createdAt: string;
}

/** Request body for create/update shareholder.
 * `idVerified` and `uboStatus` are admin-managed server-side and not writable. */
export interface ShareholderPayload {
  name: string;
  ownershipPercent: string;
  nationality: string;
}

/** GET /api/v1/companies/shareholders/ */
export async function getShareholders(): Promise<Shareholder[]> {
  const url = apiUrl("/api/v1/companies/shareholders/");
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadShareholders")),
      response.status,
      data,
    );
  }
  return Array.isArray(data) ? data : [];
}

/** POST /api/v1/companies/shareholders/ */
export async function createShareholder(body: ShareholderPayload): Promise<Shareholder> {
  const url = apiUrl("/api/v1/companies/shareholders/");
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToAddShareholder")),
      response.status,
      data,
    );
  }
  return data as Shareholder;
}

/** PUT /api/v1/companies/shareholders/{id}/ */
export async function updateShareholder(id: number, body: ShareholderPayload): Promise<Shareholder> {
  const url = apiUrl(`/api/v1/companies/shareholders/${id}/`);
  const response = await fetch(url, {
    method: "PUT",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToUpdateShareholder")),
      response.status,
      data,
    );
  }
  return data as Shareholder;
}

/** DELETE /api/v1/companies/shareholders/{id}/ */
export async function deleteShareholder(id: number): Promise<void> {
  const url = apiUrl(`/api/v1/companies/shareholders/${id}/`);
  const response = await fetch(url, {
    method: "DELETE",
    headers: getJsonHeaders(),
    credentials: "include",
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToDeleteShareholder")),
      response.status,
      data,
    );
  }
}

/** Company document from GET /api/v1/companies/documents/ */
export interface CompanyDocument {
  id: number;
  fileUrl: string;
  fileName: string;
  fileFormat: string;
  status: string;
  createdAt: string;
}

/** Request body for POST /api/v1/companies/documents/ */
export interface CompanyDocumentCreateRequest {
  fileUrl: string;
  fileName: string;
  fileFormat: string;
  status: string;
}

/** GET /api/v1/companies/documents/ */
export async function getCompanyDocuments(): Promise<CompanyDocument[]> {
  const url = apiUrl("/api/v1/companies/documents/");
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadDocuments")),
      response.status,
      data,
    );
  }
  return Array.isArray(data) ? data : [];
}

/** POST /api/v1/companies/documents/ */
export async function createCompanyDocument(body: CompanyDocumentCreateRequest): Promise<CompanyDocument> {
  const url = apiUrl("/api/v1/companies/documents/");
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToAddDocument")),
      response.status,
      data,
    );
  }
  return data as CompanyDocument;
}

/** DELETE /api/v1/companies/documents/{id}/ */
export async function deleteCompanyDocument(id: number): Promise<void> {
  const url = apiUrl(`/api/v1/companies/documents/${id}/`);
  const response = await fetch(url, {
    method: "DELETE",
    headers: getJsonHeaders(),
    credentials: "include",
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToDeleteDocument")),
      response.status,
      data,
    );
  }
}

/** Article from GET /api/v1/articles/ */
export interface Article {
  id: number;
  category: string;
  title: string;
  brief: string;
  source: string;
  releaseDate: string;
  fullArticleLink: string;
  createdAt: string;
}

/** GET /api/v1/articles/ */
export async function getArticles(): Promise<Article[]> {
  const url = apiUrl("/api/v1/articles/");
  const response = await fetch(url, {
    method: "GET",
    headers: getJsonHeaders(),
    credentials: "include",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiRequestError(
      extractApiErrorMessage(data, i18n.t("common:apiFallback.failedToLoadArticles")),
      response.status,
      data,
    );
  }
  return Array.isArray(data) ? data : [];
}

/** User object returned by login */
export interface LoginUser {
  id: number;
  email: string;
  username: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  fullName: string;
  birthdate: string | null;
  description: string | null;
  bio: string | null;
  avatar: string | null;
  isProfileComplete: boolean;
  isVerified: boolean;
  dateJoined: string;
  createdAt: string;
  updatedAt: string;
}

/** Response from login */
export interface LoginResponse {
  success?: boolean;
  data?: { user?: LoginUser };
  message?: string;
  isProfileComplete?: boolean;
  profile?: LoginUser;
}

/** Login with email and password. On success returns user data; on failure throws with message. */
export async function loginUser(email: string, password: string): Promise<LoginResponse> {
  const url = apiUrl("/api/v1/users/login/");
  const response = await fetch(url, {
    method: "POST",
    headers: getJsonHeaders(),
    credentials: "include",
    body: JSON.stringify({ email: email.trim(), password }),
  });

  const data = (await response.json().catch(() => ({}))) as LoginResponse;

  if (response.ok) {
    return data;
  }

  throw new ApiRequestError(
    extractApiErrorMessage(data, i18n.t("common:apiFallback.wrongEmailOrPassword")),
    response.status,
    data,
  );
}

// --------------------------------------------------------------------------
// Deal workspace (WP2.3) — company side
// --------------------------------------------------------------------------
// The deal workspace endpoints are not in the orval client because they're
// new in WP2.3; we hit them with hand-written fetch wrappers so the whole
// feature can ship in one commit without a schema-first regeneration churn.
// Keep them colocated with uploadFile/loginUser — same patterns, same
// `credentials: "include"` session, same ApiRequestError on failure.

export type DealWorkspaceSide = "buyer" | "supplier" | "moderator";
export type DealContractStatus = "draft" | "active" | "accepted" | "completed" | "cancelled";

export interface DealWorkspaceMember {
  userId: number;
  name: string;
  email: string | null; // only same-side callers see counterpart emails
  companyId: number;
  side: "buyer" | "supplier";
  isOwner: boolean;
}

export interface DealWorkspaceMemberCandidate {
  userId: number;
  name: string;
  email: string | null;
}

export interface DealMemberListResponse {
  members: DealWorkspaceMember[];
  candidates: DealWorkspaceMemberCandidate[];
}

export interface DealWorkspaceFileUploader {
  userId: number | null;
  name: string;
  side: "buyer" | "supplier" | "moderator";
}

export interface DealWorkspaceFile {
  id: number;
  fileName: string;
  fileFormat: string;
  size: number;
  url: string; // short-lived signed URL
  uploadedBy: DealWorkspaceFileUploader;
  createdAt: string;
}

export interface DealWorkspaceTask {
  id: number;
  title: string;
  description: string;
  dueDate: string | null;
  status: "open" | "done";
  assigneeId: number | null;
  assigneeName: string | null;
  createdByModeratorId: number | null;
  createdAt: string;
}

export interface DealWorkspaceCompanySummary {
  id: number;
  companyName: string;
  avatar?: string | null;
  [key: string]: unknown;
}

export interface DealWorkspaceTimelineItem {
  name: string;
  status: string;
  date: string | null;
}

export interface DealWorkspaceOutcome {
  status: string;
  firstMeetingAt: string | null;
  proposalAt: string | null;
  contractSignedAt: string | null;
}

export interface DealWorkspaceOverview {
  id: number;
  side: DealWorkspaceSide;
  isOwner: boolean;
  requestTitle: string;
  conversationId: number | null;
  buyer: DealWorkspaceCompanySummary;
  supplier: DealWorkspaceCompanySummary;
  createdAt: string;
  outcome: DealWorkspaceOutcome;
  timeline: DealWorkspaceTimelineItem[];
  members: DealWorkspaceMember[];
  files: DealWorkspaceFile[];
}

export interface DealContractAcceptance {
  buyerAccepted: boolean;
  buyerAcceptedAt: string | null;
  supplierAccepted: boolean;
  supplierAcceptedAt: string | null;
  acceptedVersionNumber: number;
}

export interface DealContract {
  id: number;
  status: DealContractStatus;
  contractType: string;
  contractValue: string | null;
  dateSigned: string | null;
  startDate: string | null;
  endDate: string | null;
  description: string;
  termsAndConditions: string;
  documents: string[];
  currentVersionNumber: number;
  acceptance: DealContractAcceptance;
  /** KYB contract gate: companies of the deal not verified yet (empty when both are, or the gate is off). */
  kybWarnings?: KybWarning[];
}

/** One line of the KYB contract gate (`apps/verification/gate.py`). */
export interface KybWarning {
  code: string;
  side: "buyer" | "supplier";
  companyId: number;
  companyName: string;
  message: string;
  /** Moderator responses only; companies never see the other side's verdict. */
  verdict?: string;
}

/**
 * `kybWarnings` from a contract response, or from the body of a 403
 * `kyb_verification_required` error (an `ApiRequestError` from the fetch
 * helpers or an axios error from the orval client).
 */
export function readKybWarnings(source: unknown): KybWarning[] {
  const candidates: unknown[] = [source];
  if (source && typeof source === "object") {
    const s = source as { data?: unknown; response?: { data?: unknown } };
    candidates.push(s.data, s.response?.data);
  }
  for (const body of candidates) {
    const list = body && typeof body === "object" ? (body as { kybWarnings?: unknown }).kybWarnings : undefined;
    if (Array.isArray(list)) return list as KybWarning[];
  }
  return [];
}

async function requestJson<T>(url: string, init: RequestInit = {}, fallback: string): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const resp = await fetch(apiUrl(url), {
    credentials: "include",
    ...init,
    headers,
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new ApiRequestError(extractApiErrorMessage(data, fallback), resp.status, data);
  }
  return data as T;
}

// All fallback error strings use typed i18n keys from `deals:workspace.errors.*`.
// We resolve them lazily (at call time) so i18n is initialised and locale
// switches are respected.
const dealErr = (key: string): string => i18n.t(`deals:workspace.errors.${key}` as never);

export function getDealWorkspace(id: number): Promise<DealWorkspaceOverview> {
  return requestJson<DealWorkspaceOverview>(
    `/api/v1/matchRequests/deals/${id}/`,
    { method: "GET" },
    dealErr("loadDeal"),
  );
}

export function listDealWorkspaces(): Promise<{ results: DealWorkspaceOverview[] }> {
  return requestJson(`/api/v1/matchRequests/deals/`, { method: "GET" }, dealErr("loadDeal"));
}

export function listDealFiles(id: number): Promise<DealWorkspaceFile[]> {
  return requestJson(`/api/v1/matchRequests/deals/${id}/files/`, { method: "GET" }, dealErr("loadFiles")).then(
    (data) =>
      Array.isArray(data) ? (data as DealWorkspaceFile[]) : ((data as { results?: DealWorkspaceFile[] }).results ?? []),
  );
}

export async function uploadDealFile(id: number, file: File): Promise<DealWorkspaceFile> {
  const fd = new FormData();
  fd.append("file", file);
  return requestJson<DealWorkspaceFile>(
    `/api/v1/matchRequests/deals/${id}/files/`,
    { method: "POST", body: fd },
    dealErr("uploadFile"),
  );
}

export function deleteDealFile(id: number, fileId: number): Promise<void> {
  return fetch(apiUrl(`/api/v1/matchRequests/deals/${id}/files/${fileId}/`), {
    method: "DELETE",
    credentials: "include",
  }).then(async (resp) => {
    if (!resp.ok && resp.status !== 204) {
      const data = await resp.json().catch(() => ({}));
      throw new ApiRequestError(extractApiErrorMessage(data, dealErr("deleteFile")), resp.status, data);
    }
  });
}

export function listDealMembers(id: number): Promise<DealMemberListResponse> {
  return requestJson<DealMemberListResponse>(
    `/api/v1/matchRequests/deals/${id}/members/`,
    { method: "GET" },
    dealErr("loadMembers"),
  );
}

export function addDealMember(id: number, userId: number): Promise<DealWorkspaceMember> {
  return requestJson(
    `/api/v1/matchRequests/deals/${id}/members/`,
    { method: "POST", body: JSON.stringify({ userId }) },
    dealErr("addMember"),
  );
}

export function removeDealMember(id: number, userId: number): Promise<void> {
  return fetch(apiUrl(`/api/v1/matchRequests/deals/${id}/members/${userId}/`), {
    method: "DELETE",
    credentials: "include",
  }).then(async (resp) => {
    if (!resp.ok && resp.status !== 204) {
      const data = await resp.json().catch(() => ({}));
      throw new ApiRequestError(extractApiErrorMessage(data, dealErr("removeMember")), resp.status, data);
    }
  });
}

export function listDealTasks(id: number): Promise<DealWorkspaceTask[]> {
  return requestJson(`/api/v1/matchRequests/deals/${id}/tasks/`, { method: "GET" }, dealErr("loadTasks")).then(
    (data) =>
      Array.isArray(data) ? (data as DealWorkspaceTask[]) : ((data as { results?: DealWorkspaceTask[] }).results ?? []),
  );
}

export function getDealContract(id: number): Promise<DealContract> {
  return requestJson<DealContract>(
    `/api/v1/matchRequests/deals/${id}/contract/`,
    { method: "GET" },
    dealErr("loadContract"),
  );
}

export function acceptDealContract(id: number, versionNumber: number): Promise<DealContract> {
  return requestJson<DealContract>(
    `/api/v1/matchRequests/deals/${id}/contract/accept/`,
    { method: "POST", body: JSON.stringify({ versionNumber }) },
    dealErr("acceptContract"),
  );
}

// --------------------------------------------------------------------------
// Deal workspace (WP2.3) — moderator side
// --------------------------------------------------------------------------

export interface ContractVersionRow {
  versionNumber: number;
  savedByModeratorId: number | null;
  savedByModeratorName: string | null;
  contractType: string;
  contractValue: string | null;
  dateSigned: string | null;
  status: DealContractStatus;
  startDate: string | null;
  endDate: string | null;
  description: string;
  termsAndConditions: string;
  documents: string[];
  savedAt: string;
}

export function listModeratorDealTasks(id: number): Promise<DealWorkspaceTask[]> {
  return requestJson(`/api/v1/moderators/deal-workspaces/${id}/tasks/`, { method: "GET" }, dealErr("loadTasks")).then(
    (data) =>
      Array.isArray(data) ? (data as DealWorkspaceTask[]) : ((data as { results?: DealWorkspaceTask[] }).results ?? []),
  );
}

export interface CreateDealTaskBody {
  title: string;
  description?: string;
  dueDate?: string | null;
  assigneeId?: number | null;
  status?: "open" | "done";
}

export function createModeratorDealTask(id: number, body: CreateDealTaskBody): Promise<DealWorkspaceTask> {
  return requestJson(
    `/api/v1/moderators/deal-workspaces/${id}/tasks/`,
    { method: "POST", body: JSON.stringify(body) },
    dealErr("saveTask"),
  );
}

export function patchModeratorDealTask(
  id: number,
  taskId: number,
  body: Partial<CreateDealTaskBody>,
): Promise<DealWorkspaceTask> {
  return requestJson(
    `/api/v1/moderators/deal-workspaces/${id}/tasks/${taskId}/`,
    { method: "PATCH", body: JSON.stringify(body) },
    dealErr("saveTask"),
  );
}

export function deleteModeratorDealTask(id: number, taskId: number): Promise<void> {
  return fetch(apiUrl(`/api/v1/moderators/deal-workspaces/${id}/tasks/${taskId}/`), {
    method: "DELETE",
    credentials: "include",
  }).then(async (resp) => {
    if (!resp.ok && resp.status !== 204) {
      const data = await resp.json().catch(() => ({}));
      throw new ApiRequestError(extractApiErrorMessage(data, dealErr("deleteTask")), resp.status, data);
    }
  });
}

export function listModeratorDealFiles(id: number): Promise<DealWorkspaceFile[]> {
  return requestJson(`/api/v1/moderators/deal-workspaces/${id}/files/`, { method: "GET" }, dealErr("loadFiles")).then(
    (data) =>
      Array.isArray(data) ? (data as DealWorkspaceFile[]) : ((data as { results?: DealWorkspaceFile[] }).results ?? []),
  );
}

export function getModeratorDealWorkspace(id: number): Promise<DealWorkspaceOverview> {
  return requestJson<DealWorkspaceOverview>(
    `/api/v1/moderators/deal-workspaces/${id}/`,
    { method: "GET" },
    dealErr("loadDeal"),
  );
}

export async function uploadModeratorDealFile(id: number, file: File): Promise<DealWorkspaceFile> {
  const fd = new FormData();
  fd.append("file", file);
  return requestJson<DealWorkspaceFile>(
    `/api/v1/moderators/deal-workspaces/${id}/files/`,
    { method: "POST", body: fd },
    dealErr("uploadFile"),
  );
}

export function deleteModeratorDealFile(id: number, fileId: number): Promise<void> {
  return fetch(apiUrl(`/api/v1/moderators/deal-workspaces/${id}/files/${fileId}/`), {
    method: "DELETE",
    credentials: "include",
  }).then(async (resp) => {
    if (!resp.ok && resp.status !== 204) {
      const data = await resp.json().catch(() => ({}));
      throw new ApiRequestError(extractApiErrorMessage(data, dealErr("deleteFile")), resp.status, data);
    }
  });
}

export function listModeratorContractVersions(dealId: number): Promise<ContractVersionRow[]> {
  return requestJson(
    `/api/v1/moderators/deals/${dealId}/contract/versions/`,
    { method: "GET" },
    dealErr("loadVersions"),
  ).then((data) => (Array.isArray(data) ? (data as ContractVersionRow[]) : []));
}
